/**
 * Feature E07-F04 — Review Gate typed api-client + Query hooks (web side).
 *
 * Feature-owned (NOT in the shared queries.ts / queryKeys.ts). Uses the shared
 * `apiClient` (auto-injects the session Bearer token, normalizes the response,
 * never throws) + `unwrap` (the single throw-at-the-Query-boundary helper).
 * Contracts come from `@eer/shared` (research-review.ts) so the SPA speaks the
 * exact same shapes as the Fastify API (routes/research-review.ts).
 *
 * Endpoints (routes/research-review.ts, mounted under /research by the Wire pass):
 *   GET   /research/:requestId/review                       (T05 — table payload)
 *   PATCH /research/:requestId/review/:rowId/status         (T05 accept/reject ·
 *                                                            T06 grade override)
 *   POST  /research/:requestId/review/companies             (T02 manual add)
 *   POST  /research/:requestId/review/bulk                  (T02 bulk add)
 *   POST  /research/:requestId/review/:rowId/verify         (T03 verify)
 *   POST  /research/:requestId/review/submit                (T04 submit gate)
 *
 * Each response is validated against the shared schema at the boundary so a
 * drifted shape fails loudly rather than rendering garbage. The mutations write
 * the freshly-returned row back into the cached list (so the table re-renders the
 * new review_status / grade without a refetch — the count badges follow).
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  reviewListResponseSchema,
  setStatusResponseSchema,
  addCompanyResponseSchema,
  bulkAddResponseSchema,
  verifyCompanyResponseSchema,
  submitReviewResponseSchema,
  setWebsiteResponseSchema,
  confirmWebsiteResponseSchema,
  prefilterFlagResponseSchema,
  triagePreviewResponseSchema,
  triageStartResponseSchema,
  triageProgressResponseSchema,
  triageRetriagePreviewResponseSchema,
  triageRetriageResponseSchema,
  linkRowCompanyResponseSchema,
  type TriagePreviewResponse,
  type TriageStartResponse,
  type TriageProgressResponse,
  type TriageRetriagePreviewResponse,
  type TriageRetriageResponse,
  type LinkRowCompanyResponse,
  type PrefilterFlagResponse,
  type SetWebsiteResponse,
  type ConfirmWebsiteResponse,
  type ReviewListResponse,
  type SetStatusResponse,
  type AddCompanyResponse,
  type BulkAddResponse,
  type VerifyCompanyResponse,
  type SubmitReviewResponse,
  type ReviewStatus,
  type ReviewGrade,
  type ReviewRow,
} from '@eer/shared';
import { apiClient, unwrap, type ApiResultError } from '../api/client.js';
import { triageInFlight } from './researchTriageModel.js';

// ---------------------------------------------------------------------------
// query keys (feature-local — the shared registry has no research-review domain)
// ---------------------------------------------------------------------------

export const reviewKeys = {
  all: ['research', 'review'] as const,
  list: (requestId: number) => ['research', 'review', requestId] as const,
  /** E07-F11-T02 — the Jev triage progress of a request (polled while a run is active). */
  triageProgress: (requestId: number) => ['research', 'review-triage-progress', requestId] as const,
} as const;

// ---------------------------------------------------------------------------
// fetchers
// ---------------------------------------------------------------------------

/** Fetch the Review-table payload (rows + count-badge summary + reviewMode). */
export async function fetchReviewList(requestId: number): Promise<ReviewListResponse> {
  const result = await apiClient.get<unknown>(`/research/${requestId}/review`, {
    command: 'research_review_list',
  });
  return reviewListResponseSchema.parse(unwrap(result));
}

/**
 * Set review_status (accept/reject) — the server applies the prototype TOGGLE
 * rule (`current === status ? 'pending' : status`); re-clicking the same status
 * reverts to pending (T05).
 */
export async function setReviewStatus(
  requestId: number,
  rowId: number,
  status: ReviewStatus,
): Promise<SetStatusResponse> {
  const result = await apiClient.patch<unknown>(
    `/research/${requestId}/review/${rowId}/status`,
    { kind: 'status', status },
    { command: 'research_set_status' },
  );
  return setStatusResponseSchema.parse(unwrap(result));
}

/** Override the grade (A|B|C) on a row — server logs the human override (T06). */
export async function overrideGrade(
  requestId: number,
  rowId: number,
  grade: ReviewGrade,
): Promise<SetStatusResponse> {
  const result = await apiClient.patch<unknown>(
    `/research/${requestId}/review/${rowId}/status`,
    { kind: 'grade', grade },
    { command: 'research_set_status' },
  );
  return setStatusResponseSchema.parse(unwrap(result));
}

/** Manually add ONE company (is_manual, needs_human, accepted) — T02. */
export async function addCompany(
  requestId: number,
  input: { name: string; site?: string; grade?: ReviewGrade | null },
): Promise<AddCompanyResponse> {
  const result = await apiClient.post<unknown>(`/research/${requestId}/review/companies`, input, {
    command: 'research_add_company',
  });
  return addCompanyResponseSchema.parse(unwrap(result));
}

/** Bulk add (one row per line; needs_human, pending) — T02. */
export async function bulkAddCompanies(requestId: number, text: string): Promise<BulkAddResponse> {
  const result = await apiClient.post<unknown>(
    `/research/${requestId}/review/bulk`,
    { text },
    { command: 'research_bulk_add' },
  );
  return bulkAddResponseSchema.parse(unwrap(result));
}

/** Verify (enrich + About-Us across all criteria) → clears needs_human — T03. */
export async function verifyCompany(
  requestId: number,
  rowId: number,
): Promise<VerifyCompanyResponse> {
  const result = await apiClient.post<unknown>(
    `/research/${requestId}/review/${rowId}/verify`,
    undefined,
    { command: 'research_verify_company' },
  );
  return verifyCompanyResponseSchema.parse(unwrap(result));
}

/**
 * E06-F03-T13-S03 — the user enters the website the automatic lookup did not find
 * (`acme.com` or a full URL). 400 invalid · 409 AI check running / conflicting site.
 */
export async function setReviewWebsite(
  requestId: number,
  rowId: number,
  website: string,
): Promise<SetWebsiteResponse> {
  const result = await apiClient.patch<unknown>(
    `/research/${requestId}/review/${rowId}/website`,
    { website },
    { command: 'research_set_website' },
  );
  return setWebsiteResponseSchema.parse(unwrap(result));
}

/**
 * E06-F03-T14 — confirm the website code found for a reference company.
 * 409 when there is no website / the row is not a reference company.
 */
export async function confirmReviewWebsite(
  requestId: number,
  rowId: number,
): Promise<ConfirmWebsiteResponse> {
  const result = await apiClient.post<unknown>(
    `/research/${requestId}/review/${rowId}/website/confirm`,
    undefined,
    { command: 'research_confirm_website' },
  );
  return confirmWebsiteResponseSchema.parse(unwrap(result));
}

/**
 * E07-F11-T01 — «Return» a row a stage-0 prefilter marked: clears its flags for this
 * request (the dismissal survives a re-run of the prefilters). `key` = one flag only.
 */
export async function dismissPrefilter(
  requestId: number,
  rowId: number,
  key?: string,
): Promise<PrefilterFlagResponse> {
  const result = await apiClient.post<unknown>(
    `/research/${requestId}/review/${rowId}/prefilter/dismiss`,
    key ? { key } : {},
    { command: 'research_prefilter_dismiss' },
  );
  return prefilterFlagResponseSchema.parse(unwrap(result));
}

// ---------------------------------------------------------------------------
// E07-F11-T02 / T03 — Jev triage + the human «Link» of a same-company hint
// ---------------------------------------------------------------------------

/** What the «Jev triage» confirmation shows (rows, rows via search, price). Read-only. */
export async function fetchTriagePreview(requestId: number): Promise<TriagePreviewResponse> {
  const result = await apiClient.get<unknown>(`/research/${requestId}/review/triage/preview`, {
    command: 'research_triage_preview',
  });
  return triagePreviewResponseSchema.parse(unwrap(result));
}

/** PAID — start the triage job (only after the confirmation). 202. */
export async function startTriage(requestId: number): Promise<TriageStartResponse> {
  const result = await apiClient.post<unknown>(`/research/${requestId}/review/triage`, undefined, {
    command: 'research_triage_start',
  });
  return triageStartResponseSchema.parse(unwrap(result));
}

/** The triage progress of the latest run (polled while it is active). */
export async function fetchTriageProgress(requestId: number): Promise<TriageProgressResponse> {
  const result = await apiClient.get<unknown>(`/research/${requestId}/review/triage/progress`, {
    command: 'research_triage_progress',
  });
  return triageProgressResponseSchema.parse(unwrap(result));
}

/** The confirmation of a re-triage of the selected rows. Read-only, no Jev call. */
export async function fetchRetriagePreview(
  requestId: number,
  rowIds: readonly number[],
): Promise<TriageRetriagePreviewResponse> {
  const result = await apiClient.post<unknown>(
    `/research/${requestId}/review/triage/retriage/preview`,
    { rowIds },
    { command: 'research_triage_retriage_preview' },
  );
  return triageRetriagePreviewResponseSchema.parse(unwrap(result));
}

/** PAID — re-triage the selected rows; `confirmedRows` = the count the dialog showed (409 on drift). */
export async function startRetriage(
  requestId: number,
  rowIds: readonly number[],
  confirmedRows: number,
): Promise<TriageRetriageResponse> {
  const result = await apiClient.post<unknown>(
    `/research/${requestId}/review/triage/retriage`,
    { rowIds, confirmedRows },
    { command: 'research_triage_retriage' },
  );
  return triageRetriageResponseSchema.parse(unwrap(result));
}

/** E07-F11-T03 — «Link»: point the row at an existing Library company (a person's click). */
export async function linkRowCompany(
  requestId: number,
  rowId: number,
  companyId: number,
): Promise<LinkRowCompanyResponse> {
  const result = await apiClient.post<unknown>(
    `/research/${requestId}/review/${rowId}/link-company`,
    { companyId },
    { command: 'research_link_company' },
  );
  return linkRowCompanyResponseSchema.parse(unwrap(result));
}

/** Submit the review gate (job→completed, request→in_review) — T04. */
export async function submitReview(requestId: number): Promise<SubmitReviewResponse> {
  const result = await apiClient.post<unknown>(`/research/${requestId}/review/submit`, undefined, {
    command: 'research_submit_review',
  });
  return submitReviewResponseSchema.parse(unwrap(result));
}

// ---------------------------------------------------------------------------
// cache helpers
// ---------------------------------------------------------------------------

/**
 * Splice an updated row back into the cached list response (so the table + count
 * badges re-render without a refetch). Recomputes the `accepted`/`needsHuman`
 * count badges from the new rows; the row is matched by `id`, all others kept.
 */
function patchCachedRow(
  prev: ReviewListResponse | undefined,
  row: ReviewRow,
): ReviewListResponse | undefined {
  if (!prev) return prev;
  const rows = prev.rows.map((r) => (r.id === row.id ? row : r));
  return {
    ...prev,
    rows,
    summary: {
      ...prev.summary,
      accepted: rows.filter((r) => r.reviewStatus === 'accepted').length,
      needsHuman: rows.filter((r) => r.needsHuman).length,
    },
  };
}

// ---------------------------------------------------------------------------
// hooks
// ---------------------------------------------------------------------------

/** The Review-table query (T05). */
export function useReviewList(
  requestId: number,
  enabled: boolean = true,
): UseQueryResult<ReviewListResponse, ApiResultError> {
  return useQuery<ReviewListResponse, ApiResultError>({
    queryKey: reviewKeys.list(requestId),
    queryFn: () => fetchReviewList(requestId),
    enabled: enabled && requestId > 0,
    // E07-F04-T12 — poll ONLY while a clicked "Refresh" is running on some row, and
    // stop by itself once it finished (a read; nothing paid starts from here).
    // E07-F11-T02 — and while a Jev triage works on some row (queued / running), so the
    // buckets and the per-row errors appear without a reload.
    refetchInterval: (query) =>
      (query.state.data?.rows ?? []).some((r) => r.priorCollection?.refreshing || triageInFlight(r))
        ? REVIEW_REFRESH_POLL_MS
        : false,
  });
}

/** Poll period of the Review list while a prior-collection Refresh is running. */
export const REVIEW_REFRESH_POLL_MS = 3000 as const;

/** accept/reject mutation (T05). On success patches the row into the cached list. */
export function useSetReviewStatus(
  requestId: number,
): UseMutationResult<SetStatusResponse, ApiResultError, { rowId: number; status: ReviewStatus }> {
  const queryClient = useQueryClient();
  return useMutation<SetStatusResponse, ApiResultError, { rowId: number; status: ReviewStatus }>({
    mutationFn: ({ rowId, status }) => setReviewStatus(requestId, rowId, status),
    onSuccess: (res) => {
      queryClient.setQueryData<ReviewListResponse>(reviewKeys.list(requestId), (prev) =>
        patchCachedRow(prev, res.row),
      );
    },
  });
}

/** grade-override mutation (T06). On success patches the row into the cached list. */
export function useOverrideGrade(
  requestId: number,
): UseMutationResult<SetStatusResponse, ApiResultError, { rowId: number; grade: ReviewGrade }> {
  const queryClient = useQueryClient();
  return useMutation<SetStatusResponse, ApiResultError, { rowId: number; grade: ReviewGrade }>({
    mutationFn: ({ rowId, grade }) => overrideGrade(requestId, rowId, grade),
    onSuccess: (res) => {
      queryClient.setQueryData<ReviewListResponse>(reviewKeys.list(requestId), (prev) =>
        patchCachedRow(prev, res.row),
      );
    },
  });
}

/**
 * Evidence-drawer query key for one row. Mirrors `evidenceKeys.drawer` in
 * researchEvidenceApi.ts (kept inline — that module imports this one, so importing
 * back would create a cycle). The two MUST stay identical.
 */
const evidenceDrawerKey = (requestId: number, rowId: number) =>
  ['research', 'evidence', requestId, rowId] as const;

/**
 * verify mutation (T03). On success patches the (now verified) row into the cache
 * AND invalidates that row's cached evidence drawer (E07-F05-T06 AC4): verify clears
 * needs_human server-side, so a drawer cached before the verify (global staleTime
 * 30s) would otherwise re-open with the stale amber "Needs human review" banner.
 */
export function useVerifyCompany(
  requestId: number,
): UseMutationResult<VerifyCompanyResponse, ApiResultError, number> {
  const queryClient = useQueryClient();
  return useMutation<VerifyCompanyResponse, ApiResultError, number>({
    mutationFn: (rowId) => verifyCompany(requestId, rowId),
    onSuccess: (res, rowId) => {
      queryClient.setQueryData<ReviewListResponse>(reviewKeys.list(requestId), (prev) =>
        patchCachedRow(prev, res.row),
      );
      void queryClient.invalidateQueries({ queryKey: evidenceDrawerKey(requestId, rowId) });
    },
  });
}

/**
 * E06-F03-T13-S03 — manual website mutation. On success patches the row into the
 * cache, then invalidates the Review list and the AI-check rows (the website now
 * unlocks the AI check there). Key literal mirrors `aiChainKeys.rows`.
 */
export function useSetReviewWebsite(
  requestId: number,
): UseMutationResult<SetWebsiteResponse, ApiResultError, { rowId: number; website: string }> {
  const queryClient = useQueryClient();
  return useMutation<SetWebsiteResponse, ApiResultError, { rowId: number; website: string }>({
    mutationFn: ({ rowId, website }) => setReviewWebsite(requestId, rowId, website),
    onSuccess: (res) => {
      queryClient.setQueryData<ReviewListResponse>(reviewKeys.list(requestId), (prev) =>
        patchCachedRow(prev, res.row),
      );
      void queryClient.invalidateQueries({ queryKey: reviewKeys.list(requestId) });
      void queryClient.invalidateQueries({ queryKey: ['research', 'aichain', 'rows', requestId] });
    },
  });
}

/**
 * E06-F03-T14 — confirm-website mutation. Same cache handling as the website PATCH:
 * the AI-check rows are invalidated too (their "not confirmed" warning goes away).
 */
export function useConfirmReviewWebsite(
  requestId: number,
): UseMutationResult<ConfirmWebsiteResponse, ApiResultError, { rowId: number }> {
  const queryClient = useQueryClient();
  return useMutation<ConfirmWebsiteResponse, ApiResultError, { rowId: number }>({
    mutationFn: ({ rowId }) => confirmReviewWebsite(requestId, rowId),
    onSuccess: (res) => {
      queryClient.setQueryData<ReviewListResponse>(reviewKeys.list(requestId), (prev) =>
        patchCachedRow(prev, res.row),
      );
      void queryClient.invalidateQueries({ queryKey: reviewKeys.list(requestId) });
      void queryClient.invalidateQueries({ queryKey: ['research', 'aichain', 'rows', requestId] });
    },
  });
}

/**
 * E07-F11-T01 — «Return» mutation. On success the row (now without its flags) is
 * patched into the cached list, so it leaves the «prefiltered» filter at once.
 */
export function useDismissPrefilter(
  requestId: number,
): UseMutationResult<PrefilterFlagResponse, ApiResultError, { rowId: number; key?: string }> {
  const queryClient = useQueryClient();
  return useMutation<PrefilterFlagResponse, ApiResultError, { rowId: number; key?: string }>({
    mutationFn: ({ rowId, key }) => dismissPrefilter(requestId, rowId, key),
    onSuccess: (res) => {
      queryClient.setQueryData<ReviewListResponse>(reviewKeys.list(requestId), (prev) =>
        patchCachedRow(prev, res.row),
      );
    },
  });
}

/**
 * E07-F11-T02 — triage progress of the request. Polls ONLY while a run is active (a read;
 * nothing paid starts from here) and stops by itself when the run finished.
 */
export function useTriageProgress(
  requestId: number,
  enabled: boolean = true,
): UseQueryResult<TriageProgressResponse, ApiResultError> {
  return useQuery<TriageProgressResponse, ApiResultError>({
    queryKey: reviewKeys.triageProgress(requestId),
    queryFn: () => fetchTriageProgress(requestId),
    enabled: enabled && requestId > 0,
    refetchInterval: (query) => (query.state.data?.active ? REVIEW_REFRESH_POLL_MS : false),
  });
}

/** E07-F11-T02 — the confirmation numbers, fetched when the dialog opens (a read, no Jev call). */
export function useTriagePreview(
  requestId: number,
): UseMutationResult<TriagePreviewResponse, ApiResultError, void> {
  return useMutation<TriagePreviewResponse, ApiResultError, void>({
    mutationFn: () => fetchTriagePreview(requestId),
  });
}

/** Refresh the list + the progress after a triage start (the rows are queued now). */
function invalidateTriage(queryClient: ReturnType<typeof useQueryClient>, requestId: number): void {
  void queryClient.invalidateQueries({ queryKey: reviewKeys.list(requestId) });
  void queryClient.invalidateQueries({ queryKey: reviewKeys.triageProgress(requestId) });
}

/** PAID — E07-F11-T02: start the triage. Fires ONLY from the confirmation dialog. */
export function useStartTriage(
  requestId: number,
): UseMutationResult<TriageStartResponse, ApiResultError, void> {
  const queryClient = useQueryClient();
  return useMutation<TriageStartResponse, ApiResultError, void>({
    mutationFn: () => startTriage(requestId),
    onSuccess: () => invalidateTriage(queryClient, requestId),
  });
}

/** E07-F11-T02 — the re-triage confirmation for the selected rows (a read, no Jev call). */
export function useRetriagePreview(
  requestId: number,
): UseMutationResult<TriageRetriagePreviewResponse, ApiResultError, readonly number[]> {
  return useMutation<TriageRetriagePreviewResponse, ApiResultError, readonly number[]>({
    mutationFn: (rowIds) => fetchRetriagePreview(requestId, rowIds),
  });
}

/** PAID — E07-F11-T02: re-triage the selected rows. Fires ONLY from the confirmation dialog. */
export function useStartRetriage(
  requestId: number,
): UseMutationResult<
  TriageRetriageResponse,
  ApiResultError,
  { rowIds: readonly number[]; confirmedRows: number }
> {
  const queryClient = useQueryClient();
  return useMutation<
    TriageRetriageResponse,
    ApiResultError,
    { rowIds: readonly number[]; confirmedRows: number }
  >({
    mutationFn: ({ rowIds, confirmedRows }) => startRetriage(requestId, rowIds, confirmedRows),
    onSuccess: () => invalidateTriage(queryClient, requestId),
  });
}

/**
 * E07-F11-T03 — «Link» mutation. The row (now pointing at the Library company, the hint gone)
 * is patched into the cached list; the Library is refreshed too (the link shows there).
 */
export function useLinkRowCompany(
  requestId: number,
): UseMutationResult<LinkRowCompanyResponse, ApiResultError, { rowId: number; companyId: number }> {
  const queryClient = useQueryClient();
  return useMutation<LinkRowCompanyResponse, ApiResultError, { rowId: number; companyId: number }>({
    mutationFn: ({ rowId, companyId }) => linkRowCompany(requestId, rowId, companyId),
    onSuccess: (res) => {
      queryClient.setQueryData<ReviewListResponse>(reviewKeys.list(requestId), (prev) =>
        patchCachedRow(prev, res.row),
      );
      void queryClient.invalidateQueries({ queryKey: libraryKeyPrefix });
    },
  });
}

/**
 * Library + History query-key prefixes. Mirror `researchKeys.library/history` in
 * researchLibraryApi.ts (kept inline — prefix match covers every filter/page).
 */
const libraryKeyPrefix = ['research', 'library'] as const;
const historyKeyPrefix = ['research', 'history'] as const;

/**
 * submit-review mutation (T04). On success invalidates the list (gate closed) and
 * — E06-F04-T07 — the Library (the approved companies join it on submit) and
 * History (the request's status changed), so neither shows a cached pre-submit page.
 */
export function useSubmitReview(
  requestId: number,
): UseMutationResult<SubmitReviewResponse, ApiResultError, void> {
  const queryClient = useQueryClient();
  return useMutation<SubmitReviewResponse, ApiResultError, void>({
    mutationFn: () => submitReview(requestId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reviewKeys.list(requestId) });
      void queryClient.invalidateQueries({ queryKey: libraryKeyPrefix });
      void queryClient.invalidateQueries({ queryKey: historyKeyPrefix });
    },
  });
}

/** Variables for the manual single-add mutation (T02). */
export interface AddCompanyVars {
  readonly name: string;
  readonly site?: string;
  readonly grade?: ReviewGrade | null;
}

/**
 * Manual single-add mutation (T02). On success prepends the freshly-added row to
 * the cached list (the manual row lands flagged needs-human / accepted server-side)
 * and re-derives the count badges — no refetch.
 */
export function useAddCompany(
  requestId: number,
): UseMutationResult<AddCompanyResponse, ApiResultError, AddCompanyVars> {
  const queryClient = useQueryClient();
  return useMutation<AddCompanyResponse, ApiResultError, AddCompanyVars>({
    mutationFn: (vars) => addCompany(requestId, vars),
    onSuccess: (res) => {
      queryClient.setQueryData<ReviewListResponse>(reviewKeys.list(requestId), (prev) =>
        prependRows(prev, [res.row]),
      );
    },
  });
}

/**
 * Bulk-add mutation (T02): one row per pasted line. On success prepends every
 * added row to the cached list and re-derives the count badges — no refetch.
 */
export function useBulkAddCompanies(
  requestId: number,
): UseMutationResult<BulkAddResponse, ApiResultError, string> {
  const queryClient = useQueryClient();
  return useMutation<BulkAddResponse, ApiResultError, string>({
    mutationFn: (text) => bulkAddCompanies(requestId, text),
    onSuccess: (res) => {
      queryClient.setQueryData<ReviewListResponse>(reviewKeys.list(requestId), (prev) =>
        prependRows(prev, res.rows),
      );
    },
  });
}

/**
 * Prepend freshly-added rows to the cached list + re-derive ALL summary badges
 * (total / accepted / needsHuman / discovered). E07-F04-T10: `discovered` used to
 * be left untouched here ("manual adds are not discovered"), which re-created the
 * very mismatch the bug is about — the strip claimed "52 discovered" over a
 * 53-row table until the next refetch. The server now reports `discovered` as the
 * live long-list size, so the optimistic cache must move it with the rows.
 */
function prependRows(
  prev: ReviewListResponse | undefined,
  added: readonly ReviewRow[],
): ReviewListResponse | undefined {
  if (!prev) return prev;
  const rows = [...added, ...prev.rows];
  return {
    ...prev,
    rows,
    summary: {
      ...prev.summary,
      total: rows.length,
      accepted: rows.filter((r) => r.reviewStatus === 'accepted').length,
      needsHuman: rows.filter((r) => r.needsHuman).length,
      discovered: rows.length,
    },
  };
}
